import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { ApiService } from 'src/app/_utils/api/api.service';
import { PushNotificationService } from 'src/app/_utils/push-notification/push-notification.service';
import { PwaService } from 'src/app/_utils/pwa/pwa.service';
import { StorageService } from 'src/app/_utils/storage/storage.service';
import {
  NOTIFICATION_STATE_NAME,
  SHOPCHAT_JS_URL,
  TIME_TO_RESHOW_NOTIFICATION_MODAL,
} from 'src/app/app.config';
import { TranslocoService } from '@ngneat/transloco';
import { take } from 'rxjs';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  providers: [MessageService],
})
export class NavbarComponent implements OnInit {
  showNavbar: boolean = false;
  data: any = {};
  uid: string = '';
  config: any = {};
  notificationModal: any = { visible: false, loading: false, error: null };
  notificationModel: any = { privacy_consent: false };
  telegram: string | null = null;
  offline: boolean = false;
  currentLang: string;
  userLang: string;
  languages: MenuItem[] = [];

  constructor(
    private translocoService: TranslocoService,
    private messageService: MessageService,
    protected router: Router,
    protected storageService: StorageService,
    private pwaService: PwaService,
    private apiService: ApiService,
    protected pushNotificationService: PushNotificationService,
    protected activatedRoute: ActivatedRoute
  ) {
    this.currentLang = this.translocoService.getActiveLang();
    this.userLang = typeof navigator !== 'undefined' ? navigator.language : '';

    this.activatedRoute.params.subscribe((params) => {
      if (params['uid']) {
        this.uid = params['uid'];
      }
    });
  }

  ngOnInit(): void {
    if (typeof window === 'undefined') return;

    this.translocoService
      .selectTranslate(['warning', 'pwa_offline'])
      .subscribe((translations) => {
        // check for network status
        this.pwaService.networkStatus().subscribe({
          next: (status) => {
            this.offline = !status;
            if (this.offline) {
              console.warn('Not connected, PWA offline');
              this.messageService.add({
                severity: 'warn',
                sticky: true,
                summary: translations[0],
                detail: translations[1],
              });
            } else {
              this.messageService.clear();
            }
          },
          error: (err) => console.error(err),
        });
      });

    // set the notification values for the notification model
    let notification = this.storageService.getValue(NOTIFICATION_STATE_NAME);
    if (notification && notification.status == 'subscribed') {
      this.notificationModel = {
        email: notification?.email || null,
        emailEnabled: notification?.email ? true : false,
        webpush: notification?.webpush || false,
      };
    } else {
      // show notification modal if web push is supported, for a max of 3 attempts to remind the user
      // to subscribe to the notification
      if (
        this.pushNotificationService.isSupported() &&
        this.pwaService.isInstalled()
      ) {
        let attempts = notification?.attempts || 0,
          last_attempt = notification?.last_attempt || 0;
        if (
          attempts < 3 &&
          Date.now() - last_attempt >= TIME_TO_RESHOW_NOTIFICATION_MODAL
        ) {
          setTimeout(() => (this.notificationModal.visible = true), 3000);
          this.storageService.updateValue(NOTIFICATION_STATE_NAME, {
            status: 'not-subscribed',
            attempts: attempts + 1,
            last_attempt: Date.now(),
            token: null,
            email: null,
            webpush: false,
          });
        }
      }
    }

    this.apiService.getMetadata().pipe(take(1)).subscribe({
      next: (data) => {
        if (data) {
          this.data = data.com;
          this.config = data.config;

          this.showNavbar = true;
        }
      },
      error: (err) => {
        console.error(err);
        this.router.navigate([(this.uid ? '/' + this.uid : '') + '/error'], { queryParams: { message: err } });
      },
    });
  }

  /**
   * Change current language
   * @param lang string of the language code
   */
  changeLang(lang: string) {
    const langs = ['it', ...this.data.languages];
    if (langs.includes(lang)) {
      this.currentLang = lang;
      this.translocoService.setActiveLang(lang);

      this.pwaService.getUserUID(true).subscribe({
        next: (uid) => {
          this.apiService.updateLang(uid, lang).subscribe({
            next: (res) => console.log(res),
            error: (err) => console.error(err),
          });
        },
        error: (err) => console.error(err),
      });
    } else console.error('Language not supported');
  }

  /**
   * Called when the user click the botton of the modal to update notification state
   * @param event click event
   */
  notificationClick(event: any): void {
    this.notificationModal.loading = true;
    this.notificationModal.error = null;

    if (this.notificationModel.emailEnabled == true && this.uid) {
      this.notificationModel.email = true; //TODO verifica UID API
    }

    this.pushNotificationService
      .subscribe(
        this.notificationModel.webpush || false,
        this.notificationModel.email || null
      )
      .subscribe({
        next: (token) => {
          this.notificationModal.visible = false;
          this.notificationModal.loading = false;
          this.storageService.updateValue(NOTIFICATION_STATE_NAME, {
            email: this.notificationModel.email || null,
            webpush: this.notificationModel.webpush || false,
            status: 'subscribed',
            token: token,
          });
        },
        error: (err) => {
          console.error(err);
          this.notificationModal.error = err;
          this.notificationModal.loading = false;
          this.storageService.updateValue(NOTIFICATION_STATE_NAME, {
            status: 'error',
            token: null,
            webpush: false,
            email: null,
          });
        },
      });
  }

  loadShopChatScript(shopchatUuid: string): void {
    let node = document.createElement('script');
    node.src = SHOPCHAT_JS_URL;
    node.setAttribute('public_key', shopchatUuid);
    node.setAttribute('marginBottom', '80px');
    node.setAttribute('marginRight', '20px');
    node.type = 'text/javascript';
    node.defer = true;
    document.getElementsByTagName('head')[0].appendChild(node);
  }
}
